import {useMemo} from 'react';

import {usePanelContext} from '../PanelContextProvider';
import {useIsRenderingInPopup} from '../PanelEditor/SinglePanelInspectorContainer';
import {PanelPopupMode} from '../types';
import {useDetectPanelVariant} from './hooks';

export type FullScreenPanelState = {
  /** True if the panel is full-screen via either mechanism below */
  isFullScreen: boolean;
  /**
   * True if full-screen via URL route navigation (workspace panels).
   *
   * In workspaces, clicking the expand button navigates to a dedicated route
   * (e.g. `/panel/:id` or via `?panelDisplayName=` query params). Detected by
   * `useDetectPanelVariant`, which parses `location.pathname` and search params.
   * Exiting full-screen navigates back, triggering a React re-render with the
   * standard (non-full-screen) URL — so full-screen state is inherently transient.
   */
  isFullScreenWorkspace: boolean;
  /**
   * True if full-screen via the popup modal overlay (report panels).
   *
   * In reports, full-screen has not been migrated to the URL-based flow. Instead,
   * clicking expand sets `panelPopupMode = PanelPopupMode.FULL_SCREEN` on
   * PanelContext (React state), rendering the panel inside a modal overlay wrapped
   * by `SinglePanelInspectorContextProvider`. We detect this by checking both
   * `useIsRenderingInPopup()` (are we inside that provider?) AND that the popup
   * mode is specifically `FULL_SCREEN` — not `EDIT` or `VIEW_SPEC`, which are the
   * other popup modes that also render inside the inspector provider.
   */
  isFullScreenReport: boolean;
};

/**
 * Detect whether the current panel is rendering in full-screen mode.
 *
 * Returns a structured object distinguishing between the two independent
 * full-screen mechanisms (workspace URL route vs. report popup modal).
 * See {@link FullScreenPanelState} for details on each field.
 *
 * @requires Router — This hook calls `useDetectPanelVariant`, which uses
 * `useLocation()` from react-router. It will throw if called outside a
 * `<Router>` provider. `PanelContext` and `SinglePanelInspectorContext` degrade
 * gracefully to safe defaults via their context default values
 * (`panelPopupMode: undefined`, `false`), but a missing Router is a hard crash.
 */
export const useIsFullScreenPanel = (): FullScreenPanelState => {
  const {panelPopupMode} = usePanelContext();
  const isRenderingInPopup = useIsRenderingInPopup();
  const {isFullScreenPanelPage} = useDetectPanelVariant();

  // isFullScreenPanelPage is string | null; coerce to boolean
  const isFullScreenWorkspace = !!isFullScreenPanelPage;
  const isFullScreenReport = !!(
    isRenderingInPopup && panelPopupMode === PanelPopupMode.FULL_SCREEN
  );

  return useMemo(
    () => ({
      isFullScreen: isFullScreenWorkspace || isFullScreenReport,
      isFullScreenWorkspace,
      isFullScreenReport,
    }),
    [isFullScreenWorkspace, isFullScreenReport]
  );
};
